import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import { createAppTheme } from '../../shared/theme/createAppTheme';

interface ThemeModeContextValue {
  darkState: boolean;
  handleThemeChange: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

function readStoredTheme(): boolean {
  try {
    return Boolean(JSON.parse(localStorage.getItem('THEME') ?? 'false'));
  } catch {
    return false;
  }
}

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [darkState, setDarkState] = useState(readStoredTheme);
  const location = useLocation();

  const handleThemeChange = useCallback(() => {
    setDarkState((prev) => {
      const next = !prev;
      localStorage.setItem('THEME', String(next));
      return next;
    });
  }, []);

  const activeTheme = useMemo(
    () => createAppTheme(darkState && location.pathname !== '/login'),
    [darkState, location.pathname],
  );

  const value = useMemo(
    () => ({ darkState, handleThemeChange }),
    [darkState, handleThemeChange],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={activeTheme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode(): ThemeModeContextValue {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeModeProvider');
  }
  return context;
}
